import { useMutation } from '@apollo/client'
import { FormikHelpers } from 'formik'
import qs from 'qs'
import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTracking } from 'react-tracking'
import SignUpSignIn from '..'
import LoginForm from './Form'

import { useDispatch } from 'react-redux'
import useToast from 'shared/components/Toast/useToast'
import { Login } from 'shared/graphql/auth.gql'
import { LoginFormValues } from 'shared/models/user'
import { commonRouteConfig } from 'shared/routeConfig'
import { removeUser, setRole } from 'shared/state/userSlice'
import { setToken } from 'shared/utils/auth'

const SSO_ERROR_MESSAGES: Record<string, { title: string; message: string }> = {
    no_account: {
        title: 'No account found',
        message: 'No account found for this email. Contact your admin.',
    },
    not_authorized: {
        title: 'Not authorized',
        message: 'Your account is not authorized for SSO login.',
    },
    sso_unavailable: {
        title: 'SSO unavailable',
        message: 'Single sign-on is temporarily unavailable. Try again later.',
    },
    sso_error: {
        title: 'SSO error',
        message: 'Something went wrong during sign-in. Please try again.',
    },
    invalid_state: {
        title: 'SSO session expired',
        message: 'Your sign-in session expired. Please try again.',
    },
    identity_mismatch: {
        title: 'Identity mismatch',
        message:
            'This email is already linked to a different identity. Contact your admin.',
    },
}

const SignIn = () => {
    const tracking = useTracking()
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const toast = useToast()

    const [login] = useMutation(Login)

    const queryParams = qs.parse(location.search, {
        ignoreQueryPrefix: true,
    })
    const expired = queryParams['expired']
    const toPath = queryParams['to'] ?? (location.state as any)?.to

    useEffect(() => {
        const hash = window.location.hash.substring(1)
        if (!hash) return

        const params = new URLSearchParams(hash)
        const ssoToken = params.get('token')
        const ssoError = params.get('error')

        window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search
        )

        if (ssoToken) {
            setToken(ssoToken)
            navigate(commonRouteConfig.index.path)
            return
        }

        if (ssoError) {
            const ssoMessage = params.get('message')
            const errorInfo =
                SSO_ERROR_MESSAGES[ssoError] ?? SSO_ERROR_MESSAGES.sso_error
            toast({
                title: errorInfo.title,
                message: ssoMessage
                    ? decodeURIComponent(ssoMessage)
                    : errorInfo.message,
            })
        }
    }, [navigate, toast])

    useEffect(() => {
        if (expired && toPath) {
            toast({
                title: 'Your session has timed out',
                message: 'Log in to continue',
            })

            dispatch(removeUser())

            navigate(commonRouteConfig.login.path, {
                state: { to: toPath },
            })
        }
    }, [expired, navigate, toPath, dispatch, toast])

    async function handleSubmit(
        values: LoginFormValues,
        helpers: FormikHelpers<LoginFormValues>
    ) {
        const { email, password } = values
        const loginInput = { email, password }
        try {
            const loginResponse = await login({
                variables: { input: loginInput },
            })
            if (loginResponse.data && loginResponse.data.login) {
                tracking.trackEvent({
                    url: commonRouteConfig.login.path,
                    actions: ['Session', 'Login Success'],
                })

                setToken(loginResponse.data.login.token)
                dispatch(setRole(loginResponse.data.login.role))
                return navigate(toPath ?? commonRouteConfig.index.path)
            }
        } catch (err) {
            if (
                (err as any)?.graphQLErrors[0]?.extensions?.code ===
                'REQUIRE_RESET_PASSWORD'
            ) {
                toast({
                    title: 'Your password has expired',
                    message: 'Please reset your password',
                })
                return navigate(commonRouteConfig.forgotPassword.path)
            } else if (
                (err as any)?.graphQLErrors[0]?.extensions?.code ===
                'UNAUTHENTICATED'
            ) {
                toast({
                    title: 'Invalid email or password',
                    message: 'Please try again',
                })
            } else if (
                (err as any)?.graphQLErrors[0]?.extensions?.code ===
                'SSO_REQUIRED'
            ) {
                toast({
                    title: 'SSO required',
                    message:
                        'This account uses SSO. Please use the "Sign in with SSO" button below.',
                })
            } else {
                const message = (err as any).message.replace(
                    'GraphQL error: ',
                    ''
                )
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
                console.log('Error', message)
                tracking.trackEvent({
                    url: commonRouteConfig.login.path,
                    actions: ['Session', 'Login Failed', message],
                })
                return null
            }
        } finally {
            helpers.setSubmitting(false)
        }
        return null
    }

    return (
        <SignUpSignIn title="Sign In">
            <LoginForm onSubmit={handleSubmit} />
        </SignUpSignIn>
    )
}

export default SignIn
